import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, ArrowRight } from 'lucide-react';
import type { UpdateInfo } from '../services/updateService';
import type { ServiceIntroInfo } from '../services/serviceIntroService';
import { safeStorageSet } from '../utils/safeStorage';

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  updateInfo: UpdateInfo | null;
  isLoading?: boolean;
  serviceIntroInfo?: ServiceIntroInfo | null;
  isLoadingIntro?: boolean;
  showServiceIntro?: boolean;
}

export function UpdateModal({ 
  isOpen, 
  onClose, 
  updateInfo, 
  isLoading = false,
  serviceIntroInfo = null,
  isLoadingIntro = false,
  showServiceIntro = false,
}: UpdateModalProps) {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState<'intro' | 'update'>('intro');

  // 1.3.45: 根据 showServiceIntro 初始化页面
  useEffect(() => {
    if (isOpen) {
      if (showServiceIntro && serviceIntroInfo) {
        setCurrentPage('intro');
      } else {
        setCurrentPage('update');
      }
    }
  }, [isOpen, showServiceIntro, serviceIntroInfo]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    // 1.3.48: 关闭也要写入"已读"标记，避免线上受限存储/误操作导致刷新反复弹窗
    // - 在服务介绍页关闭：视为已看过服务介绍
    if (currentPage === 'intro') {
      safeStorageSet('has_seen_service_intro', 'true');
      if (import.meta.env.DEV) {
        console.log('[UpdateModal] 服务介绍页关闭 → 写入 has_seen_service_intro = true');
      }
    }
    // - 无论在哪一页关闭：如有版本信息都记录已看过该版本（避免刷新后再弹更新页）
    if (updateInfo?.version) {
      safeStorageSet('last_seen_update_version', updateInfo.version);
      if (import.meta.env.DEV) {
        console.log('[UpdateModal] 关闭时写入 last_seen_update_version =', updateInfo.version);
      }
    }
    onClose();
  };

  const handleNext = () => {
    // 保存已看过服务介绍的标记
    safeStorageSet('has_seen_service_intro', 'true');
    if (import.meta.env.DEV) {
      console.log('[UpdateModal] 点击"下一步" → 写入 has_seen_service_intro = true');
    }
    // 切换到更新页
    setCurrentPage('update');
    // 如果没有更新内容，直接关闭弹窗
    if (!updateInfo) {
      if (import.meta.env.DEV) {
        console.log('[UpdateModal] 无更新内容 → 自动关闭弹窗');
      }
      // 延迟关闭，让动画完成
      setTimeout(() => {
        onClose();
      }, 300);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleConfirm}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 2.0.0: 双主题适配 - Modal 容器：亮色默认 + 暗色变体 */}
            <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/50 w-full max-w-md overflow-hidden">
              {/* Header */}
              {/* 2.0.0: 双主题适配 - Header border：亮色默认 + 暗色变体 */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
                {/* 2.0.0: 双主题适配 - Header 标题：亮色默认 + 暗色变体 */}
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {currentPage === 'intro' 
                    ? (isLoadingIntro ? t('serviceIntroLoading', '加载中...') : (serviceIntroInfo?.title || t('serviceIntroTitle', '服务介绍')))
                    : (isLoading ? t('updateModalLoading', '加载中...') : (updateInfo?.title || t('updateModalTitle', '功能更新')))
                  }
                </h2>
                {/* 2.0.0: 双主题适配 - 关闭按钮：亮色默认 + 暗色变体 */}
                <button
                  onClick={handleConfirm}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-white"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content Container with Slide Animation */}
              <div className="relative overflow-hidden">
                <motion.div
                  className="flex"
                  animate={{ x: currentPage === 'intro' ? '0%' : '-50%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                  style={{ width: '200%' }}
                >
                  {/* Service Intro Page */}
                  <div className="w-1/2 flex-shrink-0 p-6">
                    {isLoadingIntro ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                      </div>
                    ) : serviceIntroInfo ? (
                      <>
                        {/* Service Intro List */}
                        <div className="mb-6 space-y-3">
                          {serviceIntroInfo.intro && serviceIntroInfo.intro.length > 0 ? (
                            <ul className="space-y-2.5">
                              {serviceIntroInfo.intro.map((item, index) => (
                                <li key={index} className="flex items-start gap-3">
                                  <div className="flex-shrink-0 mt-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                                  </div>
                                  {/* 2.0.0: 双主题适配 - 内容文字：亮色默认 + 暗色变体 */}
                                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed flex-1">
                                    {item}
                                  </p>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                              {/* 2.0.0: 双主题适配 - 内容文字：亮色默认 + 暗色变体 */}
                              {t('serviceIntroNoContent', '暂无介绍内容')}
                            </p>
                          )}
                        </div>

                        {/* Next Button */}
                        <button
                          onClick={handleNext}
                          className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          {t('serviceIntroNext', '下一步')}
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        {/* 2.0.0: 双主题适配 - 次要文字：亮色默认 + 暗色变体 */}
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          {t('serviceIntroError', '无法加载服务介绍内容')}
                        </p>
                        {/* 2.0.0: 双主题适配 - 错误状态"下一步"按钮：亮色默认 + 暗色变体 */}
                        <button
                          onClick={handleNext}
                          className="mt-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-white font-medium rounded-lg transition-colors"
                        >
                          {t('serviceIntroNext', '下一步')}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Update Page */}
                  <div className="w-1/2 flex-shrink-0 p-6">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                      </div>
                    ) : updateInfo ? (
                      <>
                        {/* Update List */}
                        <div className="mb-6 space-y-3">
                          {updateInfo.updates && updateInfo.updates.length > 0 ? (
                            <ul className="space-y-2.5">
                              {updateInfo.updates.map((update, index) => (
                                <li key={index} className="flex items-start gap-3">
                                  <div className="flex-shrink-0 mt-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                                  </div>
                                  {/* 2.0.0: 双主题适配 - 内容文字：亮色默认 + 暗色变体 */}
                                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed flex-1">
                                    {update}
                                  </p>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                              {/* 2.0.0: 双主题适配 - 内容文字：亮色默认 + 暗色变体 */}
                              {t('updateModalNoUpdates', '暂无更新内容')}
                            </p>
                          )}
                        </div>

                        {/* Confirm Button */}
                        <button
                          onClick={handleConfirm}
                          className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <Check className="w-5 h-5" />
                          {t('updateModalConfirm', '知道了')}
                        </button>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        {/* 2.0.0: 双主题适配 - 次要文字：亮色默认 + 暗色变体 */}
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          {t('updateModalError', '无法加载更新内容')}
                        </p>
                        {/* 2.0.0: 双主题适配 - 更新失败"关闭"按钮：亮色默认 + 暗色变体 */}
                        <button
                          onClick={handleConfirm}
                          className="mt-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-white font-medium rounded-lg transition-colors"
                        >
                          {t('updateModalClose', '关闭')}
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

